import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from 'react-query';
import moment from 'moment/moment';
import { routeParamsSelector } from '../../CommonComponents/Router/Selector';
import { useSelectedReport } from '../state';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { DateRange } from '../../CommonComponents/OPL/dates/DateRangePicker/useDateRangePicker';
import { dateToString, SINCE_INCEPTION_LABEL, stringToDate } from '../utils/dates';
import { ReportFormatsDTO } from '../types';

export enum DependentParams {
  EntityType = 'EntityTypeSelect',
  Entity = 'EntitySelect',
}

export enum ParamType {
  Dropdown = 'dropdown',
  SingleDatePicker = 'single-date-picker',
  DateRangePickerWithPresets = 'date-picker-with-presets',
}

export enum EntityType {
  Household = '5',
  Registration = '6',
  Account = '7',
  PortfolioGroup = '107',
}

export interface OptionDTO {
  label: string;
  value: string;
  entityType: EntityType | null;
  description?: string;
  inceptionDate: string | null;
}

interface DatePeriod {
  label: string;
  range: {
    startDate: string;
    endDate: string;
  };
  value: number;
}

export interface PortfolioReportParamDTO {
  id: string;
  order: number;
  displayName: string | null;
  name: string;
  type: ParamType;
  options: OptionDTO[] | null;
  inceptionDate: string | null;
  householdInceptionDate: string | null;
  values: string[] | null;
  childParameterIds: string[] | null;
  dateRangeParameterId: string | null;
  customPeriodEnabled?: boolean;
  defaultPeriod?: DatePeriod;
  periodOptions?: DatePeriod[];
}

export interface PortfolioReportParam extends PortfolioReportParamDTO {
  range?: DateRange;
}

interface ReportDataDTO {
  reportParameters: PortfolioReportParamDTO[];
  allowedReportFormats: ReportFormatsDTO[];
}

interface PortfolioReportCache {
  params: Record<string, PortfolioReportParam>;
  order: string[];
  allowedReportFormats: ReportFormatsDTO[];
}

const fetchPortfolioReportParams = async (
  clientId: string,
  reportId: string,
): Promise<PortfolioReportCache> => {
  const { reportParameters, allowedReportFormats }: ReportDataDTO = await authFetch(
    urlWithParams('/Consumer/Reports/GetReportConfiguration', {
      reportId,
      clientId,
    }),
  );

  const params = reportParameters.reduce((acc, param) => {
    // Enhance date range param with default period (YTD)
    if (param.type === ParamType.DateRangePickerWithPresets) {
      if (!param.defaultPeriod) {
        throw new Error('No default date range in GetReportConfiguration');
      }

      acc[param.id] = {
        ...param,
        range: {
          label: param.defaultPeriod.label,
          dates: [
            stringToDate(param.defaultPeriod.range.startDate),
            stringToDate(param.defaultPeriod.range.endDate),
          ],
        },
        values: [param.defaultPeriod.range.startDate, param.defaultPeriod.range.endDate],
      };
      // Make "today" the default date for single dates
    } else if (param.type === ParamType.SingleDatePicker) {
      acc[param.id] = {
        ...param,
        values: [dateToString(new Date())],
      };
    } else {
      acc[param.id] = param;
    }

    return acc;
  }, {});

  const order = reportParameters.sort((a, b) => a.order - b.order).map(({ id }) => id);

  return {
    params,
    order,
    allowedReportFormats,
  };
};

export const PORTFOLIO_REPORT_PARAMS_QUERY_KEY = 'portfolioReportParams';

export const usePortfolioReportParamsQueryKey = () => {
  const { clientId } = useSelector(routeParamsSelector);

  const { selectedReport } = useSelectedReport();

  return [PORTFOLIO_REPORT_PARAMS_QUERY_KEY, clientId, selectedReport?.id];
};

export const usePortfolioReportCache = () => {
  const queryClient = useQueryClient();

  const queryKey = usePortfolioReportParamsQueryKey();

  const mutateParamCache = (id: string, payload: any) => {
    queryClient.setQueryData<PortfolioReportCache | undefined>(queryKey, cache => {
      if (!cache) {
        return cache;
      }

      return {
        ...cache,
        params: {
          ...cache.params,
          [id]: {
            ...cache.params[id],
            ...payload,
          },
        },
      };
    });
  };

  const mutateInceptionDate = (id: string, inceptionDate: string | null) => {
    queryClient.setQueryData<PortfolioReportCache | undefined>(queryKey, cache => {
      if (!cache) {
        return cache;
      }

      const dateRangeCache = cache.params[id];

      if (!dateRangeCache) {
        return cache;
      }

      // The "since inception" period is selected
      if (dateRangeCache.range?.label === SINCE_INCEPTION_LABEL) {
        // If inceptionDate is null use MTD as an arbitrary default period
        if (!inceptionDate) {
          const defaultDateRange = {
            label: 'MTD',
            dates: [
              moment()
                .startOf('month')
                .toDate(),
              new Date(),
            ],
          };

          return {
            ...cache,
            params: {
              ...cache.params,
              [id]: {
                ...dateRangeCache,
                inceptionDate,
                values: [
                  dateToString(defaultDateRange.dates[0]),
                  dateToString(defaultDateRange.dates[1]),
                ],
                range: defaultDateRange,
              },
            },
          };
        }

        // Update everything (inceptionDate, values and the selected range)
        return {
          ...cache,
          params: {
            ...cache.params,
            [id]: {
              ...dateRangeCache,
              inceptionDate,
              values: [inceptionDate, dateToString(new Date())],
              range: {
                ...dateRangeCache.range,
                dates: [stringToDate(inceptionDate), new Date()],
              },
            },
          },
        };
      }

      // If the "since inception" period is not selected, update just the inceptionDate field.
      if (inceptionDate) {
        return {
          ...cache,
          params: {
            ...cache.params,
            [id]: {
              ...dateRangeCache,
              inceptionDate,
            },
          },
        };
      }

      return cache;
    });
  };

  return { mutateParamCache, mutateInceptionDate };
};

export const usePortfolioReportParamsQuery = () => {
  const { clientId } = useSelector(routeParamsSelector);

  const { selectedReport } = useSelectedReport();

  const queryKey = usePortfolioReportParamsQueryKey();

  return useQuery(
    queryKey,

    () => fetchPortfolioReportParams(clientId, selectedReport!.id),

    {
      enabled: !!selectedReport?.id,
      // We are using the cache to also manage the form state (the selected values).
      // Just in case, prevent unwanted re-fetches that could overwrite it.
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );
};
